import { useRef, type PropsWithChildren, type ReactElement } from 'react';

import { StylingProps } from '@dynatrace/strato-components/core';
import type { TruncationMode } from '@dynatrace/strato-components/typography';

import { legendContainer } from './ChartLegend.sty.js';
import { ChartLegendGrid } from './components/ChartLegendGrid.js';
import { ChartVirtualizedLegendGrid } from './components/ChartVirtualizedLegendGrid.js';
import { LegendGridSettingsContext } from './context/LegendGridSettingsContext.js';
import { useLegendInteractions } from './hooks/useLegendInteractions.js';
import { LegendItem } from './types/legend.js';
import { buildGridSettings } from './utils/legend-grid.utils.js';
import { DEFAULT_TRUNCATION_MODE } from '../../../constants.js';
import { ChartLegendOrientation } from '../../../types/legend.js';
import { ORIENTATION } from '../../chart-layout/types/constants.js';
import { LegendContext } from '../shared/context/Legend.context.js';
import { useLegendSize } from '../shared/context/LegendSizeContext.js';

/**
 * @internal
 */
export interface ChartLegendProps extends StylingProps {
  /** The series of the legend */
  items: LegendItem[];
  /** Chart orientation
   ** @defaultValue 'horizontal' */
  orientation?: ChartLegendOrientation;
  /** Sometimes no legend interactions are allowed (while tooltip is pinned) */
  interactionsDisabled?: boolean;
  /** Called when the user hovers over a legend item */
  onHover?: (hoveredSeries?: LegendItem[]) => void;
  /** Called when the user click the legend item */
  onClick?: (visible: LegendItem[]) => void;
  /** Called when the user performs a legend action */
  onSeriesAction?: (
    item: LegendItem,
  ) => ReactElement<PropsWithChildren> | undefined;
  /**
   * Truncation mode to use (start, middle, end)
   * Applied to all the parts that truncate text.
   * @defaultValue 'middle'
   */
  truncationMode?: TruncationMode;
}

/**
 * @internal
 */
export const ChartLegend = (props: ChartLegendProps) => {
  const {
    items,
    orientation = ORIENTATION.Horizontal,
    onHover,
    onClick,
    onSeriesAction,
    interactionsDisabled = false,
    truncationMode = DEFAULT_TRUNCATION_MODE,
    style,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useLegendSize();
  const { onItemHover, onLegendIconClick, onLegendNameClick, hiddenItems } =
    useLegendInteractions(items, { onClick, onHover });

  const gridSettings = buildGridSettings(items, width, height);

  return (
    <div
      ref={ref}
      data-testid="chart-legend"
      className={legendContainer}
      style={{
        height,
        width,
        flexDirection: orientation === ORIENTATION.Vertical ? 'row' : 'column',
        ...style,
      }}
    >
      <LegendContext.Provider
        value={{
          hiddenItems,
          truncationMode,
          onSeriesAction,
          onLegendIconClick,
          onItemHover,
          onLegendNameClick,
          interactionsDisabled,
        }}
      >
        <LegendGridSettingsContext.Provider value={gridSettings}>
          {gridSettings.hasVirtual ? (
            <ChartVirtualizedLegendGrid
              items={items}
              orientation={orientation}
            />
          ) : (
            <ChartLegendGrid items={items} orientation={orientation} />
          )}
        </LegendGridSettingsContext.Provider>
      </LegendContext.Provider>
    </div>
  );
};

ChartLegend['displayName'] = 'ChartLegend';
